/**
 * The Errors the Design names (PRD Errors 1-5) that the builder itself decides.
 *
 * Custom field validity is not among them. An empty name and a name already in
 * use are both the Location API's to judge, and it answers on save — those
 * failures arrive through `validation/customFieldErrors.ts` instead, so there
 * is one definition of a valid custom field and it is the server's.
 */
import { isDateTimeField } from "@/util/dateTimeSerialize";
import type { GroupField } from "@/util/methods";
import type {
  BuilderContext,
  ContextElement,
  RuleResult,
  ValidationRule,
} from "../types";

const ALL_ASSETS: ValidationRule["assetTypes"] = ["form", "survey", "quiz"];
/** Payments do not exist in quizzes: SidebarStandardFields drops the whole group. */
const PAYMENT_ASSETS: ValidationRule["assetTypes"] = ["form", "survey"];

const resultFor = (
  ruleId: string,
  section: ValidationRule["section"],
  entry: ContextElement,
  params?: Record<string, string | number>,
  sidebarTab?: string
): RuleResult => ({
  ruleId,
  severity: "error",
  section,
  key: `${ruleId}:${entry.elementKey}`,
  params,
  target: {
    elementIndex: entry.elementIndex,
    slideIndex: entry.slideIndex,
    elementKey: entry.elementKey,
    sidebarTab,
  },
});

const text = (value: unknown): string =>
  typeof value === "string" ? value.trim() : "";

/**
 * The name a custom field will be created with. Mirrors what the save payload
 * sends: `customFieldLabel || label`.
 */
const customFieldName = (element: GroupField): string =>
  text((element as { customFieldLabel?: string }).customFieldLabel) ||
  text(element.label);

/**
 * True only while the field has not been created in the Location yet.
 *
 * After creation the name input is disabled and the save payload stops sending
 * `name`, so neither the empty-name nor the duplicate-name rule can apply —
 * the Location name is frozen from that point.
 */
const isUncreatedCustomField = (element: GroupField): boolean =>
  Boolean(element.custom) && !element.customEdited && !element.standard;

export const sellProductsNoProduct: ValidationRule = {
  id: "sell_products_no_product",
  severity: "error",
  assetTypes: PAYMENT_ASSETS,
  section: "payments",
  evaluate: (ctx) => {
    const paymentElements = ctx.elements.filter(
      (entry) => entry.element.tag === "payment"
    );
    if (!paymentElements.length) return [];
    // Donation mode collects an amount rather than products.
    if (ctx.payment.paymentType !== "product") return [];
    if (ctx.addedProducts.length || ctx.bumpProducts.length) return [];

    return paymentElements.map((entry) =>
      resultFor("sell_products_no_product", "payments", entry)
    );
  },
};

export const imageMissing: ValidationRule = {
  id: "image_missing",
  severity: "error",
  assetTypes: ALL_ASSETS,
  section: "fields",
  evaluate: (ctx) =>
    ctx.elements
      .filter((entry) => entry.element.type === "img")
      .filter((entry) => !text((entry.element as { url?: string }).url))
      .map((entry) => resultFor("image_missing", "fields", entry)),
};

export const scoreCalculationEmpty: ValidationRule = {
  id: "score_calculation_empty",
  severity: "error",
  assetTypes: ALL_ASSETS,
  section: "questions",
  evaluate: (ctx) =>
    ctx.elements
      .filter((entry) => entry.element.dataType === "SCORE")
      .filter((entry) => {
        const calculation = (entry.element as { calculation?: unknown })
          .calculation;
        return !Array.isArray(calculation) || calculation.length === 0;
      })
      .map((entry) =>
        // The sidebar's Options pane is relabelled "Calculations" for a SCORE
        // field, and that is where the calculation is built — Content holds the
        // field's own label and has nothing to fix.
        resultFor(
          "score_calculation_empty",
          "questions",
          entry,
          undefined,
          "Options"
        )
      ),
};

export const designErrorRules: ValidationRule[] = [
  sellProductsNoProduct,
  imageMissing,
  scoreCalculationEmpty,
];

